<template>
    <!-- Error boundary - only shows UI for critical errors by default -->
    <!-- Non-critical errors (network, validation, etc.) are still logged and reported to Sentry -->
    <slot v-if="!hasError" :key="errorKey" />
    <div v-else class="flex items-center justify-center min-h-96 p-4">
        <Card class="max-w-2xl w-full">
            <template #content>
                <div class="text-center py-8">
                    <i class="pi pi-exclamation-triangle text-6xl text-red-500 mb-6"></i>
                    <h3 class="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-4">
                        {{ $t('errors.unexpected.title') }}
                    </h3>
                    <p class="text-slate-600 dark:text-slate-400 mb-8 leading-relaxed max-w-md mx-auto">
                        {{ $t('errors.unexpected.message') }}
                    </p>

                    <div class="flex gap-3 justify-center mb-6 flex-wrap">
                        <Button @click="retry" severity="primary" icon="pi pi-refresh" :label="$t('errors.retry')" />
                        <Button @click="reportFeedback" severity="secondary" icon="pi pi-flag"
                            :label="$t('errors.report')" />
                    </div>

                    <div v-if="showDetails && isEnvDev"
                        class="mt-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-left border border-slate-200 dark:border-slate-700">
                        <h4 class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                            <i class="pi pi-code mr-2"></i>{{ $t('errors.details') }}
                        </h4>
                        <pre
                            class="text-xs text-slate-600 dark:text-slate-400 overflow-x-auto leading-relaxed whitespace-pre-wrap break-words font-mono bg-white dark:bg-slate-900 p-3 rounded border border-slate-200 dark:border-slate-600">{{ errorDetails }}</pre>
                    </div>

                    <Button v-if="isEnvDev" @click="showDetails = !showDetails" text size="small"
                        :icon="showDetails ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                        :label="showDetails ? $t('errors.hideDetails') : $t('errors.showDetails')" class="mt-4" />
                </div>
            </template>
        </Card>
    </div>
</template>

<script setup lang="ts">
import { ref, onErrorCaptured, nextTick, computed } from 'vue';
import * as Sentry from '@sentry/vue';
import Button from 'primevue/button';
import Card from 'primevue/card';
const isEnvDev = computed(() => import.meta.env.DEV);

interface Props {
    /**
     * Custom fallback component name (optional)
     */
    fallback?: string;
    /**
     * Custom error handler callback
     */
    onError?: (error: Error, instance: any, info: string) => void;
    /**
     * Show retry button
     */
    showRetry?: boolean;
    /**
     * Show report button  
     */
    showReport?: boolean;
    /**
     * Only show error boundary UI for critical errors (default: true)
     * If false, will show UI for all errors like before
     */
    onlyCriticalErrors?: boolean;
    /**
     * Additional patterns to consider as non-critical errors
     */
    additionalNonCriticalPatterns?: string[];
}

const props = withDefaults(defineProps<Props>(), {
    fallback: undefined,
    onError: undefined,
    showRetry: true,
    showReport: true,
    onlyCriticalErrors: true,
    additionalNonCriticalPatterns: () => [],
});

const hasError = ref(false);
const errorDetails = ref<string>('');
const showDetails = ref(false);
const errorKey = ref(0); // Used to force re-render child components

/**
 * Check if an error should be considered critical and trigger the error boundary UI
 */
const isCriticalError = (error: Error, info: string): boolean => {
    // If onlyCriticalErrors is disabled, consider all errors critical
    if (!props.onlyCriticalErrors) {
        return true;
    }

    const errorMessage = error.message?.toLowerCase() || '';
    const errorStack = error.stack?.toLowerCase() || '';

    // Filter out common non-critical errors that are handled elsewhere
    const nonCriticalPatterns = [
        // Network/API related errors (usually handled in components)
        'network error',
        'fetch failed',
        'failed to fetch',
        'load failed',
        'timeout',
        'aborted',

        // Authentication errors (handled by auth store)
        'authentication',
        'unauthorized',
        'token',

        // Form validation errors (handled by form components)
        'validation',
        'required',
        'invalid',

        // Router navigation errors (handled by router)
        'navigation',
        'route',

        // ResizeObserver errors (common browser issue, not critical)
        'resizeobserver loop limit exceeded',

        // Common async operation errors that are typically handled
        'promise',
        'async',

        // Add any additional patterns from props
        ...props.additionalNonCriticalPatterns
    ];

    // Check if error message matches non-critical patterns
    const isNonCritical = nonCriticalPatterns.some(pattern =>
        errorMessage.includes(pattern) || errorStack.includes(pattern)
    );

    // Consider errors during component unmounting as less critical
    const isDuringUnmount = info.includes('unmount') || info.includes('destroy');

    return !isNonCritical && !isDuringUnmount;
};

/**
 * Vue's error boundary hook - catches errors from child components
 */
onErrorCaptured((error: Error, instance, info: string) => {
    const errorDetailsText = `Error: ${error.message}\nComponent: ${instance?.$options.name || 'Unknown'}\nInfo: ${info}\nStack: ${error.stack}`;

    // Always log error for debugging (but don't always show UI)
    console.error('Error caught by boundary:', {
        error,
        component: instance?.$options.name,
        info,
        isCritical: isCriticalError(error, info)
    });

    // Only show error boundary UI for critical errors
    if (isCriticalError(error, info)) {
        hasError.value = true;
        errorDetails.value = errorDetailsText;
    }

    // Always report to Sentry but with different levels
    Sentry.withScope((scope) => {
        scope.setTag('errorBoundary', true);
        scope.setTag('vue.component', instance?.$options.name || 'Unknown');
        scope.setTag('isCritical', isCriticalError(error, info));
        scope.setContext('vue.errorInfo', {
            componentName: instance?.$options.name,
            errorInfo: info,
            propsData: instance?.$props,
        });
        // Set appropriate level based on criticality
        scope.setLevel(isCriticalError(error, info) ? 'error' : 'warning');
        Sentry.captureException(error);
    });

    // Call custom error handler if provided
    props.onError?.(error, instance, info);

    // Return false to prevent the error from propagating further
    return false;
});

const retry = async () => {
    hasError.value = false;
    errorDetails.value = '';
    showDetails.value = false;
    errorKey.value += 1; // Force re-render of child components

    // Wait for next tick to ensure DOM updates
    await nextTick();
};

const reportFeedback = () => {
    if (Sentry.lastEventId()) {
        Sentry.showReportDialog({
            eventId: Sentry.lastEventId(),
        });
    } else {
        // Fallback if no recent event
        Sentry.showReportDialog();
    }
};
</script>

<style scoped>
/* All styles converted to Tailwind classes */
</style>